/**
 * C.13a — TipTap atom node for inline Dataview blocks.
 *
 * The markdown ↔ ProseMirror conversion lives in
 * `packages/repancy/src/lib/vault/tiptap/markdownDoc.ts`. This module
 * only registers the node + wires the React view renderer.
 *
 * The React view reads `node.attrs.source` (raw DQL text), runs the
 * runner against the vault's notes, and renders a TABLE/LIST inline.
 */

import { Node } from '@tiptap/core';
import { ReactNodeViewRenderer } from '@tiptap/react';
import type { ParsedNote } from '../../../lib/vault/parse';
import { DataviewBlockView } from './DataviewBlockView';

export interface DataviewBlockAttrs {
  source: string;
}

export interface DataviewStorage {
  notes: ReadonlyArray<ParsedNote>;
  onOpenLink?: (path: string, modifier: 'pane-a' | 'pane-b') => void;
}

export const DataviewBlockNode = Node.create<Record<string, never>, DataviewStorage>({
  name: 'dataviewBlock',
  group: 'block',
  atom: true,
  selectable: true,
  draggable: false,

  addStorage() {
    return { notes: [] as ReadonlyArray<ParsedNote> };
  },

  addAttributes() {
    return {
      source: {
        default: '',
        parseHTML: (el) => el.getAttribute('data-source') ?? '',
        renderHTML: (attrs) => ({ 'data-source': (attrs as DataviewBlockAttrs).source }),
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'pre[data-dataview-block="true"]',
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'pre',
      { ...HTMLAttributes, 'data-dataview-block': 'true' },
      ['code', { class: 'language-dataview' }, HTMLAttributes['data-source'] ?? ''],
    ];
  },

  addNodeView() {
    return ReactNodeViewRenderer(DataviewBlockView);
  },
});
